<template>
    <div class="add-order-line">
        <pre
            class="debug"
            v-if="isDebug"
        >
            {{ { orderline, showErrors, hasError, productIdParam } }}
        </pre>
        <div class="top-buttons">
            <ui5-button
                class="cancel"
                @click="$emit('finished', true)"
                :disabled="saving"
            >
                <div>Annuleren</div>
            </ui5-button>
            <ui5-button
                @click="save"
                :disabled="saving"
            >
                <div>
                    <spinner v-if="saving" />
                    <span>Opslaan</span>
                </div>
            </ui5-button>
        </div>

        <h2>
            Artikel
            <template v-if="productNr">voor {{ productNr }}</template>
        </h2>

        <ui5-card>
            <div class="fields">
                <div class="row">
                    <div class="field">
                        <div class="search">
                            <div class="search-row">
                                <ui5-input
                                    type="Text"
                                    placeholder="Artikelcode"
                                    :value="profileCode"
                                    @input="profileCode = $event.target.value"
                                    @keyup.enter="searchProfile"
                                    :disabled="searching || saving"
                                />
                                <ui5-input
                                    type="Text"
                                    placeholder="Beschrijving artikel"
                                    :value="profileDescription"
                                    @input="
                                        profileDescription = $event.target.value
                                    "
                                    @keyup.enter="searchProfile"
                                    :disabled="searching || saving"
                                />
                                <ui5-button @click="searchProfile">
                                    Zoek
                                    <i class="fa fa-search" />
                                </ui5-button>
                            </div>
                            <div class="selector">
                                <ui5-select
                                    id="profile"
                                    ref="profileSelector"
                                    @change="
                                        profileId =
                                            $event.detail.selectedOption.value
                                    "
                                    :disabled="searching || saving"
                                    :class="{ inputerror: hasError.profileId }"
                                >
                                    <ui5-option
                                        :value="null"
                                        :selected="profileId == null"
                                    >
                                        Selecteer een artikel uit de zoeklijst
                                    </ui5-option>
                                    <template v-if="profiles">
                                        <ui5-option
                                            v-for="profile in profiles"
                                            :key="
                                                'profile.' + profile.profileId
                                            "
                                            :selected="
                                                profileId == profile.profileId
                                            "
                                            :value="profile.profileId"
                                        >
                                            {{ profile.code }} -
                                            {{ profile.description }} -
                                            {{ profile.supplier }}
                                        </ui5-option>
                                    </template>
                                </ui5-select>
                                <spinner v-if="searching" />
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    class="info-row"
                    v-if="profile"
                >
                    <div class="label">Eenheid</div>
                    <div class="label">Verpakking per eenheid</div>
                    <div class="label">Leverancier</div>
                    <div class="value">{{ profile.unitName || '' }}</div>
                    <div class="value">{{ profile.packagePerUnit || '' }}</div>
                    <div class="value">{{ profile.supplierName || '' }}</div>
                </div>
                <div class="amount-row">
                    <div
                        class="field"
                        v-if="profileId"
                    >
                        <text-input
                            type="number"
                            :disabled="saving"
                            placeholder="Aantal"
                            :class="{ inputerror: hasError.amount }"
                            id="amount"
                            v-model="amount"
                        />
                    </div>
                    <div
                        class="field"
                        v-if="profileId"
                    >
                        <ui5-textarea
                            id="comments"
                            :disabled="saving"
                            placeholder="Opmerkingen"
                            :value="comments"
                            @input="comments = $event.target.value"
                        />
                    </div>
                </div>
            </div>
        </ui5-card>
    </div>
</template>
<script>
import IsDebugMixin from '../../mixins/is-debug';
import IdParamMixin from '../../mixins/id-param';
import Spinner from '../../components/loader/Spinner';
import orderlineApi from '../../api/orderline';
import profileApi from '../../api/profile';
import productApi from '../../api/product';
import { isNumber } from '../../helpers/GeneralHelper';
import { showErrorMessage, showInfoMessage } from '../../dialogs';
import { mapMutations } from 'vuex';
import TextInput from '../TextInput.vue';

export default {
    components: { Spinner, TextInput },
    mixins: [IsDebugMixin, IdParamMixin('profileIdParam', 'productIdParam')],
    props: ['profileIdParam', 'productIdParam'],
    computed: {
        orderline() {
            return {
                profileId: this.profileId,
                amount: this.amount,
                comments: this.comments,
                productId: this.productId,
            };
        },
        hasError() {
            return {
                amount: this.showErrors && (!this.amount || this.amount < 1),
                profileId:
                    this.showErrors &&
                    (!this.profileId || !isNumber(this.profileId)),
            };
        },
    },
    data() {
        return {
            saving: false,
            showErrors: false,
            searching: false,
            firstProfileId: null,
            addProfileDetailsToOptions: false,
            profile: null,
            profiles: null,
            profileId: null,
            productId: null,
            productNr: null,
            profileCode: '',
            profileDescription: '',
            amount: '',
            comments: '',
        };
    },
    watch: {
        profileId() {
            this.fetchProfileInformation();
        },
    },
    methods: {
        ...mapMutations({
            addOrderedItem: 'editProduct/addOrderedItem',
        }),
        load() {
            this.profileId = null;
            this.profile = null;
            if (this.profileIdParam) {
                this.profileId = this.profileIdParam;
                this.addProfileDetailsToOptions = true;
            }
            if (this.productIdParam) {
                productApi
                    .validateProductExistence(this.productIdParam)
                    .then((response) => {
                        const productData = response.data.data;
                        this.productId = productData.productId;
                        this.productNr = productData.productNr;
                    })
                    .catch(() => {
                        showErrorMessage('Het opgegeven product bestaat niet');
                    });
            }
        },
        save() {
            this.showErrors = true;
            if (!Object.values(this.hasError).some((isError) => isError)) {
                this.saving = true;
                orderlineApi
                    .createOrderline(this.orderline)
                    .then(() => {
                        showInfoMessage('De orderlijn werd aangemaakt');
                        if (this.productId) {
                            this.addOrderedItem(this.orderline);
                        }
                        this.$emit('finished', false);
                    })
                    .catch(() => {
                        showErrorMessage('Fout bij opslaan');
                    })
                    .finally(() => {
                        this.saving = false;
                    });
            }
        },
        searchProfile() {
            const code = this.profileCode;
            const description = this.profileDescription;

            if (code.length < 2 && description.length < 2) {
                return showErrorMessage(
                    'Minstens een van de zoekvelden moet twee tekens bedragen'
                );
            }

            this.searching = true;
            profileApi
                .list(code, description)
                .then((response) => {
                    this.profiles = response.data.data;
                    if (
                        this.profileId &&
                        !this.profiles.find(
                            (x) => x.profileId == this.profileId
                        )
                    ) {
                        this.profileId = null;
                        this.profile = null;
                    }

                    if (!this.profileId) {
                        const clickEvent = new Event('click');
                        this.$refs.profileSelector.dispatchEvent(clickEvent);
                    }
                })
                .catch(() => {
                    showErrorMessage('Fout bij zoeken naar artikelen');
                })
                .finally(() => {
                    this.searching = false;
                });
        },
        fetchProfileInformation() {
            const profileId = this.profileId;

            if (!profileId) {
                this.profile = null;
                return;
            }

            this.searching = true;
            profileApi
                .fetch(profileId)
                .then((response) => {
                    const profile = response.data.data;
                    if (profile.profileId) {
                        this.profile = profile;
                        if (this.addProfileDetailsToOptions) {
                            this.addProfileDetailsToOptions = false;
                            this.profileDescription = profile.description || '';
                            this.profileCode = profile.code || '';
                            this.profiles = [{ ...profile }];
                        }
                    } else {
                        this.profileId = null;
                        this.profile = null;
                    }
                })
                .catch(() => {
                    showErrorMessage('Fout bij ophalen van artikel');
                })
                .finally(() => {
                    this.searching = false;
                });
        },
    },
};
</script>
